
/**
 * System health calculation utilities
 */

// Health status enum
export enum HealthStatus {
  GOOD = 'good',
  WARNING = 'warning',
  CRITICAL = 'critical',
}

// Health input parameters
export interface HealthInputs {
  // Essential parameters
  memoryUsage?: number;
  errorCount?: number;
  storagePercentage?: number;
  
  // Network specific
  successRate?: number;
  networkSuccessRate?: number;
  networkLatency?: number;
  networkErrors?: number;
  
  // Performance specific
  latency?: number;
  avgRenderTime?: number;
}

// Health calculation result
export interface HealthResult {
  status: HealthStatus;
  score: number;
  recommendations: string[];
}

/**
 * Calculate system health based on various metrics
 */
export const calculateSystemHealth = (inputs: HealthInputs): HealthResult => {
  const {
    memoryUsage,
    errorCount = 0,
    storagePercentage,
    successRate,
    networkSuccessRate,
    networkLatency,
    networkErrors = 0,
    latency,
    avgRenderTime
  } = inputs;
  
  // Initialize with perfect health
  let score = 100;
  const recommendations: string[] = [];
  
  // Calculate memory health
  if (memoryUsage) {
    if (memoryUsage > 80) {
      score -= 30;
      recommendations.push("Memory usage is high (>80%). Consider checking for memory leaks.");
    } else if (memoryUsage > 60) {
      score -= 10;
      recommendations.push("Memory usage is moderate (>60%). Monitor for increasing trends.");
    }
  }
  
  // Calculate error health
  if (errorCount > 5) {
    score -= 40;
    recommendations.push("Multiple errors detected. Review error logs for patterns.");
  } else if (errorCount > 0) {
    score -= 10;
    recommendations.push("System has encountered errors. Check error logs.");
  }
  
  // Calculate storage health
  if (storagePercentage) {
    if (storagePercentage > 90) {
      score -= 30;
      recommendations.push("Storage usage is critical (>90%). Clean up unnecessary data.");
    } else if (storagePercentage > 70) {
      score -= 10;
      recommendations.push("Storage usage is high (>70%). Consider cleanup options.");
    }
  }
  
  // Calculate network health
  if (networkSuccessRate !== undefined || successRate !== undefined) {
    const netSuccessRate = networkSuccessRate ?? successRate;
    
    if (netSuccessRate !== undefined) {
      if (netSuccessRate < 95) {
        score -= 30;
        recommendations.push("API success rate is low (<95%). Check network connections.");
      } else if (netSuccessRate < 98) {
        score -= 10;
        recommendations.push("API success rate is below optimal (<98%).");
      }
    }
  }
  
  // Calculate latency health
  if (networkLatency !== undefined || latency !== undefined) {
    const netLatency = networkLatency ?? latency;
    
    if (netLatency !== undefined) {
      if (netLatency > 500) {
        score -= 25;
        recommendations.push("Network latency is high (>500ms). Check for network bottlenecks.");
      } else if (netLatency > 200) {
        score -= 10;
        recommendations.push("Network latency is above optimal (>200ms).");
      }
    }
  }
  
  // Calculate render time health (if available)
  if (avgRenderTime) {
    if (avgRenderTime > 100) {
      score -= 20;
      recommendations.push("Component render times are high (>100ms). Review performance.");
    } else if (avgRenderTime > 50) {
      score -= 5;
      recommendations.push("Component render times could be improved (>50ms).");
    }
  }
  
  // Ensure score stays in valid range
  score = Math.max(0, Math.min(100, score));
  
  // Determine status based on score
  let status;
  if (score < 60 || errorCount > 5) {
    status = HealthStatus.CRITICAL;
  } else if (score < 80) {
    status = HealthStatus.WARNING;
  } else {
    status = HealthStatus.GOOD;
  }
  
  return {
    status,
    score,
    recommendations
  };
};

export default calculateSystemHealth;
